import React, { useState } from "react";
import Sidebar from "../Sidebar";
import Layout from "../../layout/Layout";
import { useQuery, useMutation, gql } from "@apollo/client";
import Loader from "../../Loader";
import CreateCampaignModal from "./CreateCampaignModal";
import CampaignDetailDrawer from "./CampaignDetailDrawer";

const GET_CAMPAIGNS = gql`
  query GetCampaigns {
    getCampaigns {
      status
      campaigns {
        id
        name
        campaignType
        campaignCategory
        triggerType
        communicationChannel
        whatsappTemplate
        incentiveType
        status
        priority
        cooldownHours
        attributionWindowHours
        batchSize
        batchIntervalHours
        audienceRules
        incentiveConfig
        metadata
        createdAt
      }
    }
  }
`;


const CREATE_CAMPAIGN = gql`
  mutation CreateCampaign($data: CreateCampaignInput!) {
    createCampaign(data: $data) {
      status
      message
      campaign {
        id
        name
      }
    }
  }
`;

const EXECUTE_CAMPAIGN = gql`
  mutation ExecuteCampaign($campaignId: ID!) {
    executeCampaign(campaignId: $campaignId) {
      status
      message
    }
  }
`;

const UPDATE_CAMPAIGN_STATUS = gql`
  mutation UpdateCampaignStatus($campaignId: ID!, $status: String!) {
    updateCampaignStatus(campaignId: $campaignId, status: $status) {
      status
      message
    }
  }
`;

const EDIT_CAMPAIGN = gql`
  mutation EditCampaign($campaignId: ID!, $data: CreateCampaignInput!) {
    editCampaign(campaignId: $campaignId, data: $data) {
      status
      message
    }
  }
`;

const HALT_CAMPAIGN_MESSAGES = gql`
  mutation HaltCampaignMessages($campaignId: ID!) {
    haltCampaignMessages(campaignId: $campaignId) {
      status
      message
      count
    }
  }
`;

const RESUME_CAMPAIGN_MESSAGES = gql`
  mutation ResumeCampaignMessages($campaignId: ID!) {
    resumeCampaignMessages(campaignId: $campaignId) {
      status
      message
      count
    }
  }
`;

const STATUS_BADGE = {
  ACTIVE: "bg-green-100 text-green-800",
  PAUSED: "bg-yellow-100 text-yellow-800",
  ARCHIVED: "bg-gray-100 text-gray-500",
  DRAFT: "bg-blue-100 text-blue-800",
};

const TRIGGER_BADGE = {
  CRON_SCHEDULED: "bg-purple-100 text-purple-800",
  EVENT_DRIVEN: "bg-indigo-100 text-indigo-800",
  MANUAL: "bg-gray-100 text-gray-700",
};

const CampaignManagement = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [executing, setExecuting] = useState(null);
  const [toast, setToast] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");

  const { data, loading, refetch } = useQuery(GET_CAMPAIGNS, {
    fetchPolicy: "network-only",
  });

  const [createCampaign] = useMutation(CREATE_CAMPAIGN);
  const [editCampaign] = useMutation(EDIT_CAMPAIGN);
  const [executeCampaign] = useMutation(EXECUTE_CAMPAIGN);
  const [updateStatus] = useMutation(UPDATE_CAMPAIGN_STATUS);
  const [haltMessages] = useMutation(HALT_CAMPAIGN_MESSAGES);
  const [resumeMessages] = useMutation(RESUME_CAMPAIGN_MESSAGES);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleCreateOrEditSubmit = async (formData) => {
    try {
      if (formData.id) {
        const { id, ...updateData } = formData;
        const res = await editCampaign({ variables: { campaignId: id, data: updateData } });
        if (res.data?.editCampaign?.status === "SUCCESS") {
          showToast("Campaign updated successfully!");
          setEditingCampaign(null);
          refetch();
        } else {
          showToast(res.data?.editCampaign?.message || "Failed to update campaign", "error");
        }
      } else {
        const res = await createCampaign({ variables: { data: formData } });
        if (res.data?.createCampaign?.status === "SUCCESS") {
          showToast("Campaign created successfully!");
          setShowCreateModal(false);
          refetch();
        } else {
          showToast(res.data?.createCampaign?.message || "Failed to create campaign", "error");
        }
      }
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  const handleExecute = async (campaign) => {
    if (!window.confirm(`Execute campaign "${campaign.name}" now?\n\nThis will resolve the audience and start journeys.`)) return;
    setExecuting(campaign.id);
    try {
      const res = await executeCampaign({ variables: { campaignId: campaign.id } });
      showToast(res.data?.executeCampaign?.message || "Campaign executed!");
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setExecuting(null);
    }
  };

  const handleToggleStatus = async (campaign) => {
    const newStatus = campaign.status === "ACTIVE" ? "PAUSED" : "ACTIVE";
    const actionWord = newStatus === "ACTIVE" ? "Resume" : "Pause";
    
    if (!window.confirm(`${actionWord} campaign "${campaign.name}"?`)) return;
    
    try {
      const res = await updateStatus({ variables: { campaignId: campaign.id, status: newStatus } });
      if (res.data?.updateCampaignStatus?.status === "SUCCESS") {
        showToast(`Campaign ${newStatus.toLowerCase()} successfully`);
        refetch();
      } else {
        showToast(res.data?.updateCampaignStatus?.message || "Failed to update status", "error");
      }
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  const handleHaltMessages = async (campaign) => {
    if (
      !window.confirm(
        `Halt pending sends for "${campaign.name}"?\n\n` +
          `Note: Pausing/activating a campaign only controls whether NEW users get enrolled — ` +
          `it does NOT stop messages that are already scheduled to go out. This will additionally ` +
          `halt every already-scheduled (but not yet sent) message for this campaign so nothing sends ` +
          `until you explicitly resume them.`,
      )
    )
      return;
    try {
      const res = await haltMessages({ variables: { campaignId: campaign.id } });
      const r = res.data?.haltCampaignMessages;
      if (r?.status === "SUCCESS") {
        showToast(r.message || `Halted ${r.count ?? 0} pending message(s).`);
      } else {
        showToast(r?.message || "Failed to halt pending messages", "error");
      }
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  const handleResumeMessages = async (campaign) => {
    if (
      !window.confirm(
        `Resume halted sends for "${campaign.name}"?\n\n` +
          `Note: this only un-halts messages you previously halted with "Halt Pending Sends" — ` +
          `they'll be re-staggered into fresh batches starting now, using this campaign's batch size/interval.`,
      )
    )
      return;
    try {
      const res = await resumeMessages({ variables: { campaignId: campaign.id } });
      const r = res.data?.resumeCampaignMessages;
      if (r?.status === "SUCCESS") {
        showToast(r.message || `Resumed ${r.count ?? 0} message(s).`);
      } else {
        showToast(r?.message || "Failed to resume pending messages", "error");
      }
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  const campaigns = data?.getCampaigns?.campaigns || [];
  const filtered = campaigns.filter((c) => {
    if (filterStatus && c.status !== filterStatus) return false;
    if (filterType && c.campaignType !== filterType) return false;
    return true;
  });

  const uniqueTypes = [...new Set(campaigns.map((c) => c.campaignType).filter(Boolean))];

  if (loading) return <Loader />;

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <Layout>
        <div className="p-6 bg-white w-full rounded-lg shadow-md m-4 overflow-y-auto">

          {/* Header */}
          <div className="flex justify-between items-center mb-6 border-b pb-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-800">Campaign Management</h1>
              <p className="text-sm text-gray-500 mt-1">
                Create and manage marketing campaigns, journeys, and incentives.
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 text-white px-5 py-2 rounded-lg shadow hover:bg-blue-700 transition font-medium"
            >
              + New Campaign
            </button>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            {[
              { label: "Total", value: campaigns.length, color: "blue" },
              { label: "Active", value: campaigns.filter((c) => c.status === "ACTIVE").length, color: "green" },
              { label: "Paused", value: campaigns.filter((c) => c.status === "PAUSED").length, color: "yellow" },
              { label: "Archived", value: campaigns.filter((c) => c.status === "ARCHIVED").length, color: "gray" },
            ].map((stat) => (
              <div key={stat.label} className={`bg-${stat.color}-50 border border-${stat.color}-100 p-4 rounded-lg text-center`}>
                <div className={`text-sm text-${stat.color}-600 font-semibold mb-1`}>{stat.label}</div>
                <div className={`text-3xl font-bold text-${stat.color}-800`}>{stat.value}</div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="flex gap-4 mb-5 bg-gray-50 p-4 rounded-lg border">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select
                className="w-full border p-2 rounded bg-white text-sm"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="ACTIVE">Active</option>
                <option value="PAUSED">Paused</option>
                <option value="DRAFT">Draft</option>
                <option value="ARCHIVED">Archived</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">Campaign Type</label>
              <select
                className="w-full border p-2 rounded bg-white text-sm"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="">All Types</option>
                {uniqueTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => { setFilterStatus(""); setFilterType(""); }}
                className="px-4 py-2 border rounded bg-white text-sm hover:bg-gray-100 transition"
              >
                Clear
              </button>
            </div>
          </div>

          {/* Campaign Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm text-gray-600">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="py-3 px-4 rounded-tl-lg">Name</th>
                  <th className="py-3 px-4">Type</th>
                  <th className="py-3 px-4">Trigger</th>
                  <th className="py-3 px-4">Channel</th>
                  <th className="py-3 px-4">Incentive</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 rounded-tr-lg">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-10 text-gray-400">
                      No campaigns found. Create your first one!
                    </td>
                  </tr>
                ) : (
                  filtered.map((campaign) => (
                    <tr key={campaign.id} className="border-b hover:bg-gray-50 transition">
                      <td className="py-3 px-4">
                        <div className="font-medium text-gray-800">{campaign.name}</div>
                        <div className="text-xs text-gray-400">P:{campaign.priority || 1} | Cooldown:{campaign.cooldownHours || 0}h</div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                          {campaign.campaignType || "—"}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`text-xs px-2 py-1 rounded font-medium ${TRIGGER_BADGE[campaign.triggerType] || "bg-gray-100 text-gray-600"}`}>
                          {campaign.triggerType || "—"}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-xs text-gray-600">{campaign.communicationChannel || "—"}</td>
                      <td className="py-3 px-4">
                        {campaign.incentiveType ? (
                          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded font-medium">
                            {campaign.incentiveType}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">None</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${STATUS_BADGE[campaign.status] || "bg-gray-100 text-gray-600"}`}>
                          {campaign.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 space-x-2 whitespace-nowrap">
                        <button
                          onClick={() => setEditingCampaign(campaign)}
                          className="text-xs px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition border border-gray-300"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setSelectedCampaign(campaign)}
                          className="text-xs px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition"
                        >
                          View
                        </button>
                        {(campaign.status === "ACTIVE" || campaign.status === "PAUSED") && (
                          <span className="inline-flex items-center" title="Pause/Resume only controls whether NEW users get enrolled — it does NOT stop messages already scheduled to send. Use 'Halt Pending Sends' for that.">
                            <button
                              onClick={() => handleToggleStatus(campaign)}
                              className={`text-xs px-3 py-1 rounded transition ${
                                campaign.status === "ACTIVE"
                                  ? "bg-yellow-100 hover:bg-yellow-200 text-yellow-800"
                                  : "bg-green-100 hover:bg-green-200 text-green-800"
                              }`}
                            >
                              {campaign.status === "ACTIVE" ? "Pause" : "Resume"}
                            </button>
                            <span className="text-gray-400 text-xs ml-1 cursor-help">ⓘ</span>
                          </span>
                        )}
                        {(campaign.status === "ACTIVE" || campaign.status === "PAUSED") && (
                          <button
                            onClick={() => handleHaltMessages(campaign)}
                            title="Stops all already-scheduled (not yet sent) messages for this campaign — separate from Pause/Resume above"
                            className="text-xs px-3 py-1 bg-red-100 hover:bg-red-200 text-red-800 rounded transition"
                          >
                            ⛔ Halt Pending Sends
                          </button>
                        )}
                        {campaign.status === "PAUSED" && (
                          <button
                            onClick={() => handleResumeMessages(campaign)}
                            title="Resumes messages previously halted with 'Halt Pending Sends', re-staggered into fresh batches starting now"
                            className="text-xs px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-800 rounded transition"
                          >
                            ▶ Resume Pending Sends
                          </button>
                        )}
                        {campaign.status === "ACTIVE" && campaign.triggerType !== "EVENT_DRIVEN" && (
                          <button
                            onClick={() => handleExecute(campaign)}
                            disabled={executing === campaign.id}
                            className="text-xs px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded transition disabled:opacity-50"
                          >
                            {executing === campaign.id ? "Running..." : "Execute Now"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Layout>

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-lg shadow-lg text-white text-sm font-medium transition ${toast.type === "error" ? "bg-red-600" : "bg-green-600"}`}>
          {toast.msg}
        </div>
      )}

      {showCreateModal && (
        <CreateCampaignModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateOrEditSubmit}
        />
      )}

      {/* Edit Modal */}
      {editingCampaign && (
        <CreateCampaignModal
          initialData={editingCampaign}
          onClose={() => setEditingCampaign(null)}
          onSubmit={handleCreateOrEditSubmit}
        />
      )}

      {/* Detail Drawer */}
      {selectedCampaign && (
        <CampaignDetailDrawer
          campaign={selectedCampaign}
          onClose={() => setSelectedCampaign(null)}
          onExecute={handleExecute}
          executing={executing}
          onHalt={handleHaltMessages}
          onResume={handleResumeMessages}
        />
      )}
    </div>
  );
};

export default CampaignManagement;
